import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FiLogOut, FiUser, FiSun, FiMoon, FiMonitor, FiCreditCard, FiLoader, FiGlobe } from 'react-icons/fi';
import { useColorContext } from '../context/colorContext';
import { useUserContext } from '../context/userContext';
import { useLanguageContext } from '../context/languageContext';
import { useUserLimitContext } from '../context/user_limit_context';
import PayementOptionPopup from './payementOptionPopup';
import { getCachedStripePlans, loadStripePlans } from '../services/stripePlansCache';
import './settingsPage.css';

const ANIM_STYLE_ID = 'plynote-settings-loader-anim';
if (typeof document !== 'undefined') {
  const existingStyleEl = document.getElementById(ANIM_STYLE_ID);
  const spinnerKeyframes = `
    @keyframes settingsSpinnerSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes settingsBackdropIn { from { opacity: 0; } to { opacity: 1; } }
  `;

  if (!existingStyleEl) {
    const styleEl = document.createElement('style');
    styleEl.id = ANIM_STYLE_ID;
    styleEl.textContent = spinnerKeyframes;
    document.head.appendChild(styleEl);
  } else if (!String(existingStyleEl.textContent || '').includes('@keyframes settingsSpinnerSpin')) {
    existingStyleEl.textContent = `${existingStyleEl.textContent || ''}\n${spinnerKeyframes}`;
  }
}

const BACKEND_BASE_URL = String(process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001').trim().replace(/\/$/, '');

const toSafeHex = (hex, fallback) => {
  const value = String(hex || '').trim();
  return /^#([A-Fa-f0-9]{6})$/.test(value) ? value : fallback;
};

const hexToRgba = (hex, alpha, fallback = '#000000') => {
  const safeHex = toSafeHex(hex, fallback);
  const r = parseInt(safeHex.slice(1, 3), 16);
  const g = parseInt(safeHex.slice(3, 5), 16);
  const b = parseInt(safeHex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

function SettingsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const themeSectionRef = useRef(null);
  const {
    bgPrimary,
    bgSecondary,
    textPrimary,
    textSecondary,
    activeAccent,
    theme_type,
    setTheme_type,
  } = useColorContext();
  const {
    name,
    first_name,
    last_name,
    username,
    email,
    user_id,
    is_loading,
    logout,
  } = useUserContext();
  const { refreshSubscriptionState } = useUserLimitContext();
  const { t, language, setLanguage } = useLanguageContext();

  const isLightTheme = String(theme_type || '').toLowerCase() === 'clear';

  const safeBgPrimary = toSafeHex(bgPrimary, isLightTheme ? '#F6F8FC' : '#0D1117');
  const safeBgSecondary = toSafeHex(bgSecondary, isLightTheme ? '#FFFFFF' : '#151B23');
  const safeTextPrimary = toSafeHex(textPrimary, isLightTheme ? '#111827' : '#E6EDF3');
  const safeTextSecondary = toSafeHex(textSecondary, isLightTheme ? '#475569' : '#9BA7B4');
  const safeAccent = toSafeHex(activeAccent, '#2EA043');
  const destructiveTextColor = isLightTheme ? '#000000' : '#FCA5A5';
  const destructiveBackground = isLightTheme ? 'rgba(239, 68, 68, 0.16)' : 'rgba(239, 68, 68, 0.12)';
  const destructiveBorder = isLightTheme ? 'rgba(220, 38, 38, 0.42)' : 'rgba(239, 68, 68, 0.35)';

  const pageBackground = useMemo(() =>
    `linear-gradient(135deg, ${safeBgPrimary} 0%, ${hexToRgba(safeAccent, 0.05, safeAccent)} 50%, ${safeBgPrimary} 100%)`,
  [safeBgPrimary, safeAccent]);

  const sectionStyle = useMemo(() => ({
    borderRadius: 20,
    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.65, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.45, safeBgPrimary)} 100%)`,
    border: `1px solid ${hexToRgba(safeAccent, 0.1, safeAccent)}`,
    padding: '24px',
  }), [safeBgSecondary, safeBgPrimary, safeAccent]);

  const infoTileStyle = useMemo(() => ({
    borderRadius: 14,
    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.3, safeBgPrimary)} 100%)`,
    border: `1px solid ${hexToRgba(safeAccent, 0.09, safeAccent)}`,
    padding: '12px 14px',
  }), [safeBgSecondary, safeBgPrimary, safeAccent]);

  const primaryBtnStyle = useMemo(() => ({
    background: `linear-gradient(270deg, ${safeAccent}, #a855f7, #0d0d1a, #a855f7, ${safeAccent})`,
    backgroundSize: '300% 300%',
    border: 'none',
    borderRadius: 12,
    color: '#FFFFFF',
    minHeight: 44,
    padding: '0 20px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    fontSize: '0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: `0 12px 28px ${hexToRgba(safeAccent, 0.22, safeAccent)}`,
    transition: 'box-shadow 260ms cubic-bezier(0.22, 1, 0.36, 1), filter 220ms cubic-bezier(0.22, 1, 0.36, 1), transform 150ms ease',
  }), [safeAccent]);

  const displayName = String(name || `${first_name || ''} ${last_name || ''}` || username || email || 'User').trim();
  const [planLoading, setPlanLoading] = useState(false);
  const [planError, setPlanError] = useState('');
  const [currentPlan, setCurrentPlan] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [showUpgradePopup, setShowUpgradePopup] = useState(false);

  const autoCheckoutFiredRef = useRef(false);
  const [autoCheckoutLoading, setAutoCheckoutLoading] = useState(false);
  const [autoCheckoutError, setAutoCheckoutError] = useState('');

  const [manageSubLoading, setManageSubLoading] = useState(false);
  const [manageSubError, setManageSubError] = useState('');

  const planFromUrl = useMemo(() => {
    const p = new URLSearchParams(location.search || '').get('plan');
    return p && p !== 'free' ? p : '';
  }, [location.search]);

  const accountRows = [
    { label: 'Full name', value: displayName || 'Not provided' },
    { label: t('settings.username'), value: username || t('settings.notProvided') },
    { label: t('settings.email'), value: email || t('settings.notProvided') },
  ];

  const themeOptions = [
    { key: 'clear', label: t('settings.themeLight'), icon: <FiSun size={16} /> },
    { key: 'dark', label: t('settings.themeDark'), icon: <FiMoon size={16} /> },
    { key: 'system', label: t('settings.themeSystem'), icon: <FiMonitor size={16} /> },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const formatPrice = (plan) => {
    if (!plan) return '$0 USD';
    if (typeof plan.amount === 'number') {
      const value = (plan.amount / 100).toFixed(2);
      const currency = String(plan.currency || 'eur').toUpperCase();
      const interval = String(plan.interval || plan.billingInterval || '').toLowerCase();
      const intervalText = interval === 'month' ? t('settings.perMonth') : interval === 'year' ? t('settings.perYear') : '';
      return `${value} ${currency} ${intervalText}`.trim();
    }
    return '$0 USD';
  };

  const normalizedPlan = useMemo(() => {
    if (!currentPlan || typeof currentPlan !== 'object') {
      return null;
    }

    return {
      ...currentPlan,
      productName: currentPlan.productName || currentPlan.planName || null,
      planKey: currentPlan.planKey || currentPlan.plan_key || null,
      status: currentPlan.status || currentPlan.subscriptionState || 'inactive',
      interval: currentPlan.interval || currentPlan.billingInterval || null,
      currentPeriodEnd: currentPlan.currentPeriodEnd || currentPlan.current_period_end || null,
      cancelAtPeriodEnd: Boolean(currentPlan.cancelAtPeriodEnd || currentPlan.cancel_at_period_end),
    };
  }, [currentPlan]);

  const isPageLoading = Boolean(is_loading) || (planLoading && !currentPlan);

  const fetchCurrentPlan = async () => {
    if (!user_id && !email) return;
    setPlanLoading(true);
    setPlanError('');
    try {
      const params = new URLSearchParams();
      if (user_id) params.set('userId', user_id);
      if (email) params.set('email', email);
      const url = `${BACKEND_BASE_URL}/api/payment/current-subscription?${params.toString()}`;
      const response = await fetch(url);
      const raw = await response.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (parseError) {
        throw new Error('Invalid server response for current plan.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Unable to load current plan.');
      }

      const hasActiveSub = Boolean(data.hasSubscription);
      setHasSubscription(hasActiveSub);
      // Only set currentPlan if subscription is active, not if it's canceled
      setCurrentPlan(hasActiveSub ? (data.subscription || null) : null);
    } catch (error) {
      setPlanError(error.message || 'Unknown error.');
      setHasSubscription(false);
      setCurrentPlan(null);
    } finally {
      setPlanLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    if (!hasSubscription || !user_id) return;
    setManageSubLoading(true);
    setManageSubError('');
    try {
      const response = await fetch(`${BACKEND_BASE_URL}/api/payment/create-billing-portal-session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user_id, email }),
      });
      const raw = await response.text();
      let data;
      try {
        data = JSON.parse(raw);
      } catch (parseError) {
        throw new Error('Invalid server response.');
      }

      if (!response.ok) {
        throw new Error(data.error || 'Unable to access billing portal.');
      }

      if (!data.url) {
        throw new Error('Billing portal URL missing.');
      }

      // Redirect to Stripe Billing Portal
      window.location.assign(data.url);
    } catch (error) {
      setManageSubError(error.message || 'Unknown error.');
      setManageSubLoading(false);
    }
  };

  // Auto-trigger Stripe checkout when arriving with ?plan= from the pricing page
  useEffect(() => {
    if (!planFromUrl || is_loading || autoCheckoutFiredRef.current) return;
    if (!user_id && !email) return;
    autoCheckoutFiredRef.current = true;
    setAutoCheckoutLoading(true);
    setAutoCheckoutError('');
    const doCheckout = async () => {
      try {
        let plans = getCachedStripePlans();
        if (plans.length === 0) plans = await loadStripePlans();
        const matched = plans.find((p) => String(p.planKey || '').toLowerCase() === planFromUrl.toLowerCase());
        if (!matched) throw new Error(`Plan "${planFromUrl}" not found in Stripe.`);
        const res = await fetch(`${BACKEND_BASE_URL}/api/payment/create-subscription-checkout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ planKey: matched.planKey, userId: user_id, customerEmail: email, customerName: name }),
        });
        const raw = await res.text();
        let data;
        try { data = JSON.parse(raw); } catch { throw new Error('Invalid server response.'); }
        if (!res.ok) throw new Error(data.error || 'Unable to launch checkout.');
        if (!data.checkoutUrl) throw new Error('Checkout URL missing.');
        // Replace current history entry with a clean URL so pressing "back" from Stripe
        // returns to /settings without ?plan= and doesn't re-trigger checkout
        window.history.replaceState({}, '', '/settings');
        window.location.assign(data.checkoutUrl);
      } catch (err) {
        setAutoCheckoutError(err.message || 'Checkout error.');
        setAutoCheckoutLoading(false);
      }
    };
    doCheckout();
  }, [planFromUrl, is_loading, user_id, email, name]);

  // Handle Stripe checkout success callback (?checkout=success&session_id=...)
  useEffect(() => {
    const search = new URLSearchParams(location.search || '');
    if (search.get('checkout') !== 'success' || !search.get('session_id') || !user_id) return;
    let cancelled = false;
    fetch(`${BACKEND_BASE_URL}/api/payment/sync-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ sessionId: search.get('session_id'), userId: user_id }),
    })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) {
          fetchCurrentPlan();
          refreshSubscriptionState?.().catch?.(() => {});
          navigate('/settings', { replace: true });
        }
      });
    return () => { cancelled = true; };
  }, [location.search, user_id, navigate, refreshSubscriptionState]);

  useEffect(() => {
    if (location.hash === '#theme' && themeSectionRef.current) {
      themeSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [location.hash]);

  useEffect(() => {
    fetchCurrentPlan();
  }, [email, user_id]);

  if (autoCheckoutLoading) {
    return (
      <div style={{
        width: '100%', height: '100dvh', overflow: 'hidden',
        display: 'grid', placeItems: 'center', padding: '28px 24px',
        background: pageBackground,
        animation: 'settingsBackdropIn .2s ease both',
      }}>
        <div style={{
          width: 'min(480px, 100%)', borderRadius: 24,
          border: `1px solid ${hexToRgba(safeAccent, 0.12, safeAccent)}`,
          background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.65, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.45, safeBgPrimary)} 100%)`,
          boxShadow: `0 24px 60px ${hexToRgba(safeBgPrimary, 0.28, safeBgPrimary)}`,
          padding: '36px 28px',
          display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 16,
        }}>
          <div aria-hidden="true" style={{ width: 56, height: 56, borderRadius: '50%', animation: 'settingsSpinnerSpin .95s linear infinite' }}>
            <svg width="56" height="56" viewBox="0 0 56 56" role="presentation">
              <circle cx="28" cy="28" r="22" fill="none"
                stroke={hexToRgba(safeAccent, 0.9, safeAccent)}
                strokeWidth="4" strokeLinecap="round" strokeDasharray="74 140"
              />
            </svg>
          </div>
          <div style={{ color: safeTextPrimary, fontSize: '1.1rem', fontWeight: 720, letterSpacing: '-0.01em' }}>
            {t('settings.redirectingToStripe')}
          </div>
          <div style={{ color: safeTextSecondary, fontSize: '0.9rem', lineHeight: 1.5 }}>
            {t('settings.redirectingDesc')}
          </div>
        </div>
      </div>
    );
  }

  if (isPageLoading) {
    return (
      <div
        style={{
          width: '100%',
          height: '100dvh',
          overflow: 'hidden',
          display: 'grid',
          placeItems: 'center',
          padding: '28px 24px',
          background: pageBackground,
          animation: 'settingsBackdropIn .2s ease both',
        }}
      >
        <div
          style={{
            width: 'min(540px, 100%)',
            borderRadius: 24,
            border: `1px solid ${hexToRgba(safeAccent, 0.12, safeAccent)}`,
            background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.65, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.45, safeBgPrimary)} 100%)`,
            boxShadow: `0 24px 60px ${hexToRgba(safeBgPrimary, 0.28, safeBgPrimary)}`,
            padding: '28px 24px 26px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            gap: 14,
          }}
        >
          <div
            aria-hidden="true"
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              animation: 'settingsSpinnerSpin .95s linear infinite',
            }}
          >
            <svg width="56" height="56" viewBox="0 0 56 56" role="presentation">
              <circle
                cx="28"
                cy="28"
                r="22"
                fill="none"
                stroke={hexToRgba(safeTextPrimary, 0.12, safeTextPrimary)}
                strokeWidth="4"
                strokeLinecap="round"
                strokeDasharray="74 140"
              />
            </svg>
          </div>

          <div style={{ color: safeTextPrimary, fontSize: '1.05rem', fontWeight: 720, letterSpacing: '-0.01em' }}>
            {t('settings.loading')}
          </div>
          <div style={{ color: safeTextSecondary, fontSize: '0.92rem', lineHeight: 1.5 }}>
            {t('settings.loadingDesc')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: '100%',
        height: '100dvh',
        background: pageBackground,
        overflowY: 'auto',
        padding: '28px 24px 36px',
      }}
    >
      <div style={{ width: 'min(980px, 100%)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 18 }}>
        <header
          style={{
            borderRadius: 20,
            border: `1px solid ${hexToRgba(safeAccent, 0.14, safeAccent)}`,
            background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.7, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.5, safeBgPrimary)} 100%)`,
            padding: '22px 24px 20px',
          }}
        >
          <div style={{ fontSize: '1.5rem', fontWeight: 760, color: safeTextPrimary, letterSpacing: '0.01em' }}>{t('settings.title')}</div>
          <div style={{ marginTop: 6, color: safeTextSecondary, fontSize: '0.92rem' }}>{t('settings.subtitle')}</div>
        </header>

        {autoCheckoutError && (
          <div style={{
            borderRadius: 14,
            border: `1px solid ${destructiveBorder}`,
            background: destructiveBackground,
            padding: '14px 16px',
            display: 'flex', flexDirection: 'column', gap: 10,
          }}>
            <div style={{ color: destructiveTextColor, fontWeight: 650, fontSize: '0.92rem' }}>
              {t('settings.stripeRedirectFailed')}
            </div>
            <div style={{ color: destructiveTextColor, fontSize: '0.86rem', lineHeight: 1.45 }}>
              {autoCheckoutError}
            </div>
            <button
              type="button"
              className="settings-hover-button"
              onClick={() => setShowUpgradePopup(true)}
              style={{
                alignSelf: 'flex-start',
                border: `1px solid ${destructiveBorder}`,
                background: 'transparent',
                color: destructiveTextColor,
                borderRadius: 10, padding: '7px 14px',
                cursor: 'pointer', fontSize: '0.85rem', fontWeight: 640,
              }}
            >
              {t('settings.retrySubscriptions')}
            </button>
          </div>
        )}

        <section
          ref={themeSectionRef}
          style={{
            ...sectionStyle,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <div style={{ width: 30, height: 30, borderRadius: 10, display: 'grid', placeItems: 'center', color: hexToRgba(safeAccent, 0.95, safeAccent), background: hexToRgba(safeAccent, 0.16, safeAccent) }}>
              <FiUser size={16} />
            </div>
            <div>
              <div style={{ color: safeTextPrimary, fontWeight: 700, fontSize: '1.02rem' }}>{t('settings.accountInfo')}</div>
              <div style={{ color: safeTextSecondary, fontSize: '0.86rem' }}>{t('settings.accountInfoDesc')}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
            {accountRows.map((row) => (
              <div key={row.label} style={infoTileStyle}>
                <div style={{ color: safeTextSecondary, fontSize: '0.76rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>{row.label}</div>
                <div style={{ color: safeTextPrimary, fontSize: '0.92rem', fontWeight: 600, wordBreak: 'break-word' }}>{row.value}</div>
              </div>
            ))}
          </div>
        </section>

        <section
          style={{
            ...sectionStyle,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 10, display: 'grid', placeItems: 'center', color: hexToRgba(safeAccent, 0.95, safeAccent), background: hexToRgba(safeAccent, 0.16, safeAccent) }}>
              <FiCreditCard size={16} />
            </div>
            <div>
              <div style={{ color: safeTextPrimary, fontWeight: 700, fontSize: '1.02rem' }}>{t('settings.currentPlan')}</div>
              <div style={{ color: safeTextSecondary, fontSize: '0.86rem' }}>{t('settings.currentPlanDesc')}</div>
            </div>
          </div>

          {planLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: safeTextSecondary, fontSize: '0.9rem', marginBottom: 10 }}>
              <FiLoader size={14} /> {t('settings.loadingPlan')}
            </div>
          ) : null}

          {planError ? (
            <div style={{
              marginBottom: 10,
              color: destructiveTextColor,
              background: destructiveBackground,
              border: `1px solid ${destructiveBorder}`,
              borderRadius: 10,
              padding: '8px 10px',
              fontSize: '0.86rem',
            }}>
              {planError}
            </div>
          ) : null}

          {manageSubError ? (
            <div style={{
              marginBottom: 10,
              color: destructiveTextColor,
              background: destructiveBackground,
              border: `1px solid ${destructiveBorder}`,
              borderRadius: 10,
              padding: '8px 10px',
              fontSize: '0.86rem',
            }}>
              {manageSubError}
            </div>
          ) : null}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 12 }}>
            <div style={infoTileStyle}>
              <div style={{ color: safeTextSecondary, fontSize: '0.74rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>{t('settings.planLabel')}</div>
              <div style={{ color: safeTextPrimary, fontSize: '0.92rem', fontWeight: 650 }}>{normalizedPlan?.productName || (hasSubscription ? t('settings.activeSubscription') : t('settings.noSubscription'))}</div>
            </div>
            <div style={infoTileStyle}>
              <div style={{ color: safeTextSecondary, fontSize: '0.74rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>{t('settings.statusLabel')}</div>
              <div style={{ color: safeTextPrimary, fontSize: '0.92rem', fontWeight: 650 }}>{normalizedPlan?.status || 'inactive'}</div>
            </div>
            <div style={infoTileStyle}>
              <div style={{ color: safeTextSecondary, fontSize: '0.74rem', letterSpacing: '0.04em', textTransform: 'uppercase', marginBottom: 4 }}>{t('settings.priceLabel')}</div>
              <div style={{ color: safeTextPrimary, fontSize: '0.92rem', fontWeight: 650 }}>{formatPrice(normalizedPlan)}</div>
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            <button
              className="settings-hover-button"
              type="button"
              onClick={() => setShowUpgradePopup(true)}
              style={primaryBtnStyle}
            >
              {t('settings.upgrade')}
            </button>
            {hasSubscription ? (
              <button
                className="settings-hover-button"
                type="button"
                onClick={handleManageSubscription}
                disabled={manageSubLoading}
                style={{
                  border: `1px solid ${hexToRgba(safeTextSecondary, 0.3, safeTextSecondary)}`,
                  background: 'transparent',
                  color: safeTextPrimary,
                  borderRadius: 12,
                  minHeight: 42,
                  padding: '0 18px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 8,
                  fontSize: '0.88rem',
                  fontWeight: 680,
                  cursor: manageSubLoading ? 'not-allowed' : 'pointer',
                  opacity: manageSubLoading ? 0.6 : 1,
                  transition: 'all 0.2s ease',
                }}
              >
                {manageSubLoading ? <FiLoader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <FiCreditCard size={16} />}
                {t('settings.manageSubscription')}
              </button>
            ) : null}
          </div>
        </section>

        <section
          style={{
            ...sectionStyle,
          }}
        >
          <div style={{ color: safeTextPrimary, fontWeight: 700, fontSize: '1.02rem', marginBottom: 4 }}>{t('settings.theme')}</div>
          <div style={{ color: safeTextSecondary, fontSize: '0.86rem', marginBottom: 14 }}>{t('settings.themeDesc')}</div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {themeOptions.map((option) => {
              const isSelected = String(theme_type || '').toLowerCase() === option.key;
              return (
                <button
                  className="settings-hover-button"
                  key={option.key}
                  type="button"
                  onClick={() => setTheme_type(option.key)}
                  style={isSelected ? {
                    ...primaryBtnStyle,
                    justifyContent: 'center',
                    minHeight: 44,
                    width: '100%',
                    fontSize: '0.9rem',
                    fontWeight: 650,
                  } : {
                    borderRadius: 12,
                    border: `1px solid ${hexToRgba(safeAccent, 0.15, safeAccent)}`,
                    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.3, safeBgPrimary)} 100%)`,
                    color: safeTextSecondary,
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {option.icon}
                  {option.label}
                </button>
              );
            })}
          </div>
        </section>

        <section style={{ ...sectionStyle }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <div style={{ width: 30, height: 30, borderRadius: 10, display: 'grid', placeItems: 'center', color: hexToRgba(safeAccent, 0.95, safeAccent), background: hexToRgba(safeAccent, 0.16, safeAccent) }}>
              <FiGlobe size={16} />
            </div>
            <div>
              <div style={{ color: safeTextPrimary, fontWeight: 700, fontSize: '1.02rem' }}>{t('settings.language')}</div>
              <div style={{ color: safeTextSecondary, fontSize: '0.86rem' }}>{t('settings.languageDesc')}</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
            {[{ key: 'en', label: t('settings.languageEn') }, { key: 'fr', label: t('settings.languageFr') }].map((option) => {
              const isSelected = language === option.key;
              return (
                <button
                  className="settings-hover-button"
                  key={option.key}
                  type="button"
                  onClick={() => setLanguage(option.key)}
                  style={isSelected ? {
                    ...primaryBtnStyle,
                    justifyContent: 'center',
                    minHeight: 44,
                    width: '100%',
                    fontSize: '0.9rem',
                    fontWeight: 650,
                  } : {
                    borderRadius: 12,
                    border: `1px solid ${hexToRgba(safeAccent, 0.15, safeAccent)}`,
                    background: `linear-gradient(135deg, ${hexToRgba(safeBgSecondary, 0.5, safeBgSecondary)} 0%, ${hexToRgba(safeBgPrimary, 0.3, safeBgPrimary)} 100%)`,
                    color: safeTextSecondary,
                    minHeight: 44,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: 600,
                  }}
                >
                  {option.label}
                </button>
              );
            })}
          </div>
        </section>

        <section
          style={{
            ...sectionStyle,
          }}
        >
          <div style={{ color: safeTextPrimary, fontWeight: 700, fontSize: '1.02rem', marginBottom: 4 }}>{t('settings.session')}</div>
          <div style={{ color: safeTextSecondary, fontSize: '0.86rem', marginBottom: 14 }}>{t('settings.sessionDesc')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            className="settings-hover-button"
            type="button"
            onClick={handleLogout}
            style={primaryBtnStyle}
          >
            <FiLogOut size={16} />
            {t('settings.signOut')}
          </button>
          </div>
        </section>
      </div>

      <PayementOptionPopup
        isOpen={showUpgradePopup}
        onClose={() => setShowUpgradePopup(false)}
        currentPlanKey={normalizedPlan?.planKey}
      />
    </div>
  );
}

export default SettingsPage;